import React, {useEffect, useRef, useState} from "react";
import {FaCalendarDays, FaListCheck, FaPaperclip, FaTag, FaTrash, FaUsers} from "react-icons/fa6";
import AttachesTable from "../../../components/attaches-table";
import Button from "../../../components/button";
import InputRadio from "../../../components/input-radio";
import Modal from "../../../components/modal";
import CardDatePicker from "./card-date-picker";
import EditableTitle from "./editable-title";
import RichDescriptionEditor from "./rich-description-editor";
import CardActivityPanel from "./card-details-modal/card-activity-panel";
import CardChecklistsSection from "./card-details-modal/card-checklists-section";
import CardLabelsPopover from "./card-details-modal/card-labels-popover";
import CardMembersPopover from "./card-details-modal/card-members-popover";
import DetailSection from "./card-details-modal/detail-section";

const detailOptions = [
    {id: "labels", label: "Etiquetas", icon: FaTag},
    {id: "dates", label: "Datas", icon: FaCalendarDays},
    {id: "checklist", label: "Checklist", icon: FaListCheck},
    {id: "members", label: "Membros", icon: FaUsers},
    {id: "attachments", label: "Anexos", icon: FaPaperclip},
];

const CardDetailsModal = ({card, listName, users = [], boardLabels = [], onCreateBoardLabel, onUpdateBoardLabel, onDeleteBoardLabel, onCreateComment, onUpdateComment, onDeleteComment, onClose, onUpdate, onDelete, isSaving, readOnly = false}) => {
    const [visibleDetails, setVisibleDetails] = useState(() => ({
        labels: (card.labels ?? []).length > 0,
        dates: Boolean(card.startDateValue || card.dueDateValue || card.reminderEnabled),
        checklist: (card.checklists ?? []).length > 0 || (card.checklist ?? []).length > 0,
        members: (card.assignees ?? []).length > 0,
        attachments: (card.attachments ?? []).length > 0,
    }));
    const [detailToOpen, setDetailToOpen] = useState(null);
    const attachmentsRef = useRef();
    const labels = card.labels ?? [];
    const members = card.assignees ?? [];

    useEffect(() => {
        if (!readOnly) return;
        setVisibleDetails({
            labels: (card.labels ?? []).length > 0,
            dates: Boolean(card.startDateValue || card.dueDateValue || card.reminderEnabled),
            checklist: (card.checklists ?? []).length > 0 || (card.checklist ?? []).length > 0,
            members: (card.assignees ?? []).length > 0,
            attachments: (card.attachments ?? []).length > 0,
        });
    }, [card.assignees, card.attachments, card.checklist, card.checklists, card.dueDateValue, card.labels, card.reminderEnabled, card.startDateValue, readOnly]);

    const showDetailAndOpen = id => {
        setVisibleDetails(current => ({...current, [id]: true}));
        if (["labels", "dates", "members"].includes(id)) setDetailToOpen(id);
    };

    const hideDetail = id => setVisibleDetails(current => ({...current, [id]: false}));

    const attachmentMetadata = attachments => JSON.stringify((attachments ?? []).map(attachment => (
        Object.fromEntries(Object.entries(attachment).filter(([field]) => field !== "file"))
    )));
    const updateAttachments = attachments => {
        if (attachmentMetadata(attachments) === attachmentMetadata(card.attachments)) return;
        onUpdate({attachments}, "Alterou os anexos");
    };

    const removeDetail = id => {
        const emptyValues = {
            labels: {labels: []},
            dates: {startDateValue: "", dueDateValue: "", dueDate: "", dueTime: "", recurrence: "never", reminder: "none", reminderEnabled: false},
            checklist: {checklists: [], checklist: []},
            members: {assignees: []},
            attachments: {attachments: []},
        };
        onUpdate(emptyValues[id], `removeu o campo ${detailOptions.find(option => option.id === id)?.label.toLowerCase()}`);
        hideDetail(id);
    };

    return (
        <Modal isOpen onClose={onClose} className="w-11/12 max-w-7xl" headerClass="!bg-slate-50 !px-5 !py-3" title={<div className="flex min-w-0 items-center gap-2 text-sm text-slate-600"><span className="truncate">{listName || "Lista"}</span></div>}>
            <div className="flex h-[calc(100vh-120px)] min-h-[560px] flex-col bg-slate-50">
                <div className="grid min-h-0 flex-1 overflow-y-auto lg:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)] lg:overflow-hidden">
                    <div className="flex min-h-0 flex-col overflow-y-auto p-5">
                        <div className="mb-5 flex items-start gap-3">
                            <InputRadio id={`card-details-completed-${card.id}`} name={`card-details-completed-${card.id}`} checked={Boolean(card.completed)} disabled={readOnly} onChange={() => {}} onClick={() => onUpdate({completed: !card.completed}, card.completed ? "Marcou o cartão como incompleto" : "Marcou o cartão como concluído")} label={card.completed ? "Reabrir cartão" : "Marcar como concluído"} className="mt-1.5 shrink-0 [&_input]:cursor-pointer [&_input]:accent-emerald-600 [&_label]:sr-only" title="Marcar como concluído" />
                            <div className="min-w-0 flex-1">
                                <EditableTitle value={card.title} onSave={title => onUpdate({title}, "Alterou o título")} label="Título do cartão" className="w-full text-xl font-bold text-slate-800" inputClassName="w-full" allowEmpty readOnly={readOnly} />
                            </div>
                        </div>

                        <div className="relative mb-2 flex flex-wrap gap-2">
                            {detailOptions.filter(({id}) => !visibleDetails[id] && (!readOnly || (id === "attachments" && (card.attachments ?? []).length > 0))).map(({id, label, icon: Icon}) => (
                                <Button key={id} type="button" onClick={() => showDetailAndOpen(id)} col="!border-slate-300 !px-3 !shadow-none"><Icon /> {label}</Button>
                            ))}
                        </div>

                        <div className="flex flex-1 flex-col gap-4">
                            <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
                                {visibleDetails.members && <CardMembersPopover key={`members-${card.id}`} members={members} availableUsers={users} onUpdate={onUpdate} onRemove={() => removeDetail("members")} openOnMount={detailToOpen === "members"} onOpenOnMount={() => setDetailToOpen(null)} readOnly={readOnly} />}

                                {visibleDetails.labels && <CardLabelsPopover key={`labels-${card.id}`} labels={labels} boardLabels={boardLabels} onUpdate={onUpdate} onCreateBoardLabel={onCreateBoardLabel} onUpdateBoardLabel={onUpdateBoardLabel} onDeleteBoardLabel={onDeleteBoardLabel} onRemove={() => removeDetail("labels")} openOnMount={detailToOpen === "labels"} onOpenOnMount={() => setDetailToOpen(null)} readOnly={readOnly} />}

                                {visibleDetails.dates && <CardDatePicker card={card} onSave={changes => onUpdate(changes, "alterou as datas do cartão")} onRemove={() => removeDetail("dates")} openOnMount={detailToOpen === "dates"} onOpenOnMount={() => setDetailToOpen(null)} readOnly={readOnly} />}
                            </div>

                            <DetailSection icon={FaListCheck} title="Descrição" removeBtn={false} className="flex min-h-[308px] min-w-0 grow shrink-0 basis-auto flex-col">
                                <RichDescriptionEditor key={card.id} value={card.description} onChange={description => onUpdate({description}, "Alterou a descrição")} className="flex min-h-0 grow shrink-0 basis-auto flex-col" contentClassName="grow shrink-0 basis-auto" readOnly={readOnly} />
                            </DetailSection>

                            {visibleDetails.checklist && <CardChecklistsSection key={`checklists-${card.id}`} card={card} onUpdate={onUpdate} onRemove={() => removeDetail("checklist")} onEmpty={() => hideDetail("checklist")} readOnly={readOnly} />}

                            {visibleDetails.attachments && (
                                <DetailSection icon={FaPaperclip} title="Anexos" onRemove={() => removeDetail("attachments")} removeBtn={!readOnly}>
                                    <AttachesTable
                                        attaches={card.attachments ?? []}
                                        gridRef={attachmentsRef}
                                        fileId={`demand-card-${card.id}-file-input`}
                                        wrapperClass="-mt-4"
                                        header={false}
                                        onAttachesChange={updateAttachments}
                                        readOnly={readOnly}
                                    />
                                </DetailSection>
                            )}
                        </div>
                    </div>

                    <CardActivityPanel key={`activity-${card.id}`} card={card} onCreateComment={onCreateComment} onUpdateComment={onUpdateComment} onDeleteComment={onDeleteComment} />
                </div>

                <footer className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-white px-5 py-4">
                    {!readOnly && <Button type="button" target="danger" onClick={onDelete}><FaTrash /> Excluir cartão</Button>}
                    {readOnly && <span className="text-xs font-medium text-slate-500">Modo de visualização</span>}
                    {isSaving && <span className="text-xs font-medium text-slate-400" role="status">Salvando alterações...</span>}
                </footer>
            </div>
        </Modal>
    );
};

export default CardDetailsModal;
